import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import axios from "axios";
import MayrLogo from "@/components/MayrLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/login`, { email, password });
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_email", data.email);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#030303] flex items-center justify-center px-6">
      <div className="max-w-sm w-full">
        <div className="flex justify-center mb-10">
          <MayrLogo height={30} />
        </div>

        <div className="mb-8 text-center">
          <div className="mx-auto mb-5 w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
            <Lock className="w-6 h-6 text-neutral-500" strokeWidth={1.5} />
          </div>
          <h1 data-testid="admin-login-title" className="font-['Outfit'] text-2xl font-medium tracking-tight text-white mb-2">
            Admin Login
          </h1>
          <p className="text-sm text-neutral-500 font-['Manrope']">
            Sign in to manage site content
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          {error && (
            <div data-testid="login-error" className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400 font-['Manrope']">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-neutral-500 font-['Manrope'] mb-2 uppercase tracking-wider">
              Email
            </label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm font-['Manrope']
                placeholder:text-neutral-600
                transition-colors duration-300
                focus:outline-none focus:border-white/[0.2] focus:bg-white/[0.06]"
              placeholder="admin@mayr-technologies.com"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-500 font-['Manrope'] mb-2 uppercase tracking-wider">
              Password
            </label>
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm font-['Manrope']
                placeholder:text-neutral-600
                transition-colors duration-300
                focus:outline-none focus:border-white/[0.2] focus:bg-white/[0.06]"
              placeholder="Enter password"
            />
          </div>

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full mt-2 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white text-[#030303] text-sm font-semibold tracking-tight font-['Manrope']
              transition-all duration-300
              hover:bg-neutral-200
              disabled:opacity-50 disabled:cursor-not-allowed
              focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#030303]"
          >
            {loading ? "Signing in..." : "Sign In"}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </button>
        </form>
        <p className="mt-4 text-center text-sm"><a href="/api/auth/recovery" className="text-neutral-300 hover:underline">Forgot password?</a></p>

        <div className="mt-8 text-center">
          <button
            data-testid="admin-back-to-site"
            onClick={() => navigate("/")}
            className="text-xs text-neutral-600 font-['Manrope'] transition-colors duration-300 hover:text-neutral-400"
          >
            Back to site
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
